import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, Calendar, Receipt, Users, Scissors, UserCog, Package, BarChart3, Settings as SettingsIcon, LogOut, Menu, Bell, X, User, Globe } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n';
import { useSalonProfile } from '@/contexts/SalonProfileContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import api from '@/lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'hinglish', label: 'Hinglish' },
];

function formatNotificationTime(createdAt, t) {
  if (!createdAt) return t('just_now');
  const date = new Date(createdAt);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  if (diffMins < 1) return t('just_now');
  if (diffMins < 60) return `${diffMins} ${t('min_ago')}`;
  if (diffHours === 1) return t('hour_ago');
  return `${diffHours} ${t('hours_ago')}`;
}

const NOTIFICATION_TYPE_STYLE = {
  low_stock:   { icon: '⚠️', bg: 'bg-amber-50/70' },
  booking:     { icon: '📅', bg: 'bg-blue-50/70' },
  payment:     { icon: '✅', bg: 'bg-green-50/70' },
  customer:    { icon: '👤', bg: 'bg-purple-50/70' },
  staff:       { icon: '👥', bg: 'bg-teal-50/70' },
  attendance:  { icon: '🕐', bg: 'bg-teal-50/70' },
  inventory:   { icon: '📦', bg: 'bg-amber-50/70' },
  service:    { icon: '✂️', bg: 'bg-pink-50/70' },
  system:      { icon: 'ℹ️', bg: 'bg-gray-50/70' },
};
function notificationIcon(type) {
  return NOTIFICATION_TYPE_STYLE[type]?.icon ?? 'ℹ️';
}
function notificationBg(type) {
  return NOTIFICATION_TYPE_STYLE[type]?.bg ?? 'bg-gray-50/70';
}

const navConfig = [
  { path: '/', key: 'nav_dashboard', icon: LayoutDashboard },
  { path: '/bookings', key: 'nav_bookings', icon: Calendar },
  { path: '/billing', key: 'nav_billing', icon: Receipt },
  { path: '/customers', key: 'nav_customers', icon: Users },
  { path: '/services', key: 'nav_services', icon: Scissors },
  { path: '/staff', key: 'nav_staff', icon: UserCog },
  { path: '/inventory', key: 'nav_inventory', icon: Package },
  { path: '/reports', key: 'nav_reports', icon: BarChart3 },
  { path: '/settings', key: 'settings', icon: SettingsIcon },
];

/** Paths match `navConfig`; dashboard is `/` (same as `/dashboard` conceptually). */
const NAV_ACCESS = {
  '/': ['owner', 'manager', 'receptionist', 'stylist'],
  '/bookings': ['owner', 'manager', 'receptionist', 'stylist'],
  '/billing': ['owner', 'manager', 'receptionist'],
  '/customers': ['owner', 'manager', 'receptionist'],
  '/services': ['owner', 'manager'],
  '/staff': ['owner', 'manager'],
  '/inventory': ['owner', 'manager'],
  '/reports': ['owner', 'manager'],
  '/settings': ['owner'],
};

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const wasOfflineRef = useRef(!navigator.onLine);
  const { t, language, setLanguage } = useLanguage();
  const user = JSON.parse(localStorage.getItem('salon_user') || '{}');
  const userRole = (user.role || 'stylist').toLowerCase();
  const canAccessSettings = userRole === 'owner';
  const visibleNavItems = navConfig.filter((item) => NAV_ACCESS[item.path]?.includes(userRole));
  const { salonName, refreshProfile } = useSalonProfile();

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [deleteSingleId, setDeleteSingleId] = useState(null);
  const fetchNotifications = useCallback(async () => {
    if (!localStorage.getItem('salon_token')) return;
    setNotificationsLoading(true);
    try {
      const res = await api.get('/notifications');
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('salon_token')) refreshProfile();
  }, [refreshProfile]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleOnline = () => {
      setOffline(false);
      if (wasOfflineRef.current) {
        toast.success(t('offline_toast_restored'));
        wasOfflineRef.current = false;
      }
    };
    const handleOffline = () => {
      setOffline(true);
      wasOfflineRef.current = true;
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [t]);

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const badgeLabel = unreadCount === 0 ? null : unreadCount >= 10 ? '9+' : String(unreadCount);

  const markNotificationRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (_) {}
  };

  const markAllRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (_) {}
  };

  const handleNotificationClick = (n) => {
    if (selectMode) return;
    if (!n.is_read) markNotificationRead(n.id);
    if (n.link) navigate(n.link);
  };

  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setDeleteSingleId(null);
      toast.success(t('notif_deleted'));
    } catch (_) {
      toast.error(t('delete_failed'));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      const res = await api.delete('/notifications', { data: { ids } });
      const count = res.data?.deleted_count ?? ids.length;
      setNotifications(prev => prev.filter(n => !selectedIds.has(n.id)));
      setSelectedIds(new Set());
      setSelectMode(false);
      toast.success(t('notif_deleted_count', { count }));
    } catch (_) {
      toast.error(t('delete_failed'));
    }
  };

  const deleteAllNotifications = async () => {
    try {
      const res = await api.delete('/notifications', { data: { delete_all: true } });
      setNotifications([]);
      setDeleteAllConfirmOpen(false);
      setSelectMode(false);
      setSelectedIds(new Set());
      toast.success(t('notif_deleted'));
    } catch (_) {
      toast.error(t('delete_failed'));
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelectMode(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSavePassword = async () => {
    if (!newPassword.trim()) return;
    if (newPassword !== confirmPassword) {
      toast.error(t('password_mismatch'));
      return;
    }
    if (!currentPassword.trim()) {
      toast.error(t('password_required'));
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put('/users/me', { current_password: currentPassword || undefined, password: newPassword });
      toast.success(t('password_changed'));
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setProfileModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('password_update_failed'));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('salon_token');
    localStorage.removeItem('salon_user');
    navigate('/login');
  };

  const openLogoutConfirm = () => setLogoutConfirmOpen(true);
  const openProfileModal = () => setProfileModalOpen(true);

  const SidebarContent = ({ onNavClick }) => (
    <div className="flex flex-col h-full">
      <div className="p-6 pb-4">
        <h1 className="font-poppins text-2xl font-bold text-[#f0a500] tracking-wide">SalonSync</h1>
        <p className="text-sm text-gray-400 mt-1 truncate">{salonName || user.salon_name || 'Black Shine'}</p>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {visibleNavItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onNavClick}
              data-testid={`nav-${item.key.replace('nav_', '')}`}
              className={`flex items-center gap-3 px-4 py-3.5 rounded-xl text-base font-medium transition-all duration-200 ${
                active
                  ? 'bg-[#f0a500] text-[#1a1a2e] shadow-lg shadow-[#f0a500]/20'
                  : 'text-gray-300 hover:bg-white/10 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span>{t(item.key)}</span>
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-white/10">
        <LanguageSwitcher />
      </div>
      <div className="px-3 pb-4">
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-4 py-3 w-full text-gray-400 hover:text-white rounded-xl hover:bg-white/10 transition-all text-base"
        >
          <LogOut className="w-5 h-5" />
          <span>{t('nav_logout')}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-[#faf8f5]">
      {/* Offline banner - full width, pushes content down */}
      {offline && (
        <div className="w-full flex-shrink-0 bg-red-500/90 text-white px-4 py-3 text-center text-base font-medium flex items-center justify-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" aria-hidden />
          {t('offline_banner')}
        </div>
      )}
      <div className="flex flex-1 min-h-0">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-[260px] bg-[#1a1a2e] flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[280px] bg-[#1a1a2e] shadow-2xl">
            <div className="absolute top-4 right-4">
              <button onClick={() => setMobileOpen(false)} className="text-gray-400 hover:text-white p-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent onNavClick={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-100 px-4 lg:px-8 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h2 className="font-poppins text-xl font-semibold text-[#1a1a2e]">
              {t(visibleNavItems.find((n) => n.path === location.pathname)?.key || navConfig.find((n) => n.path === location.pathname)?.key || 'nav_dashboard')}
            </h2>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative w-10 h-10 sm:w-10 sm:h-10 rounded-xl" data-testid="notifications-btn">
                  <Bell className="w-5 h-5 text-gray-500" />
                  {badgeLabel != null && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-white text-[10px] flex items-center justify-center font-bold">
                      {badgeLabel}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[min(100vw-2rem,360px)] rounded-2xl border-gray-100 shadow-lg p-0">
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 flex-wrap">
                  {selectMode ? (
                    <>
                      <span className="font-poppins font-semibold text-[#1a1a2e]">{selectedIds.size} {t('notif_selected')}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={deleteSelected}
                          disabled={selectedIds.size === 0}
                          className={`text-sm font-medium rounded-lg px-3 py-1.5 ${selectedIds.size > 0 ? 'bg-[#f0a500] text-[#1a1a2e] hover:bg-[#d99400]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                        >
                          {t('notif_delete_selected')}
                        </button>
                        <button type="button" onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }} className="text-sm text-gray-600 hover:text-[#1a1a2e] font-medium">
                          {t('select_cancel')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="font-poppins font-semibold text-[#1a1a2e]">{t('notifications')}</span>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setSelectMode(true)} className="text-sm text-[#1a1a2e] hover:text-[#f0a500] font-medium">
                          {t('notif_select')}
                        </button>
                        {unreadCount > 0 && (
                          <button type="button" onClick={markAllRead} className="text-sm text-[#f0a500] hover:text-[#d99400] font-medium">
                            {t('mark_all_read')} ✓
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="max-h-[70vh] overflow-y-auto">
                  {notificationsLoading && notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-gray-500 text-base">{t('loading')}</div>
                  ) : notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <p className="text-gray-500 text-base mb-2">{t('no_notifications')}</p>
                      <span className="text-4xl" aria-hidden>🔔</span>
                    </div>
                  ) : (
                    <>
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`group relative border-b border-gray-50 ${selectMode ? '' : 'cursor-pointer'} transition-colors hover:bg-gray-50/80 ${!n.is_read ? notificationBg(n.type) : 'bg-white'}`}
                        >
                          {deleteSingleId === n.id ? (
                            <div className="px-4 py-3 flex items-center justify-between gap-2">
                              <span className="text-sm text-gray-600">{t('notif_delete_single_confirm')}</span>
                              <div className="flex gap-1">
                                <button type="button" onClick={() => setDeleteSingleId(null)} className="text-sm px-2 py-1 rounded-lg border border-gray-200 hover:bg-gray-50">{t('cancel')}</button>
                                <button type="button" onClick={() => deleteNotification(n.id)} className="text-sm px-2 py-1 rounded-lg bg-red-500 text-white hover:bg-red-600">{t('confirm')}</button>
                              </div>
                            </div>
                          ) : (
                            <div
                              role={selectMode ? undefined : 'button'}
                              onClick={() => selectMode ? toggleSelectId(n.id) : handleNotificationClick(n)}
                              className="flex items-start gap-2 px-4 py-3 text-left min-h-[60px]"
                            >
                              {selectMode && (
                                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                                  <Checkbox
                                    checked={selectedIds.has(n.id)}
                                    onCheckedChange={() => toggleSelectId(n.id)}
                                    className="border-[#1a1a2e]"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              )}
                              <span className="text-lg flex-shrink-0">{notificationIcon(n.type)}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <span className="font-medium text-[#1a1a2e] text-sm">{n.title_key ? t(n.title_key) : n.title}</span>
                                  <span className="text-xs text-gray-400 flex-shrink-0">{formatNotificationTime(n.created_at, t)}</span>
                                </div>
                                <p className="text-sm text-gray-600 mt-0.5">{n.message_key ? t(n.message_key, n.message_params || {}) : n.message}</p>
                                {n.link && (
                                  <span className="inline-block mt-1 text-xs text-[#f0a500] font-medium">
                                    → {n.link === '/inventory' ? t('view_inventory') : n.link === '/bookings' ? t('view_bookings') : n.link === '/settings' ? t('settings') : n.link === '/customers' ? t('nav_customers') : n.link === '/staff' ? t('nav_staff') : n.link === '/services' ? t('nav_services') : n.link === '/billing' ? t('nav_billing') : n.link}
                                  </span>
                                )}
                              </div>
                              {!selectMode && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setDeleteSingleId(n.id); }}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 flex-shrink-0 transition-opacity"
                                  aria-label="Delete"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="px-4 py-2 flex items-center justify-between border-t border-gray-50">
                        <span className="text-xs text-gray-400">{t('older_notifications')}</span>
                        <button type="button" onClick={() => setDeleteAllConfirmOpen(true)} className="text-xs text-red-500 hover:text-red-600 font-medium">
                          {t('notif_delete_all')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="hidden sm:flex items-center gap-2.5 bg-[#faf8f5] hover:bg-[#f0a500]/10 px-3 py-2 rounded-xl transition-colors border border-transparent hover:border-[#f0a500]/20"
                  data-testid="profile-menu-trigger"
                >
                  <div className="w-9 h-9 bg-[#f0a500] rounded-full flex items-center justify-center text-[#1a1a2e] font-bold text-sm">
                    {(user.name || 'User').trim()[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-[#1a1a2e] leading-tight">{user.name || 'User'}</p>
                    <p className="text-xs text-gray-500 capitalize">{user.role || 'owner'} · {salonName || user.salon_name || 'Salon'}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[min(100vw-2rem,280px)] rounded-2xl border-gray-100 shadow-lg">
                <div className="px-3 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 bg-[#f0a500] rounded-full flex items-center justify-center text-[#1a1a2e] font-bold text-sm">
                      {(user.name || 'User').trim()[0]?.toUpperCase() || 'U'}
                    </div>
                    <div>
                      <p className="font-semibold text-[#1a1a2e]">{user.name || 'User'}</p>
                      <p className="text-xs text-gray-500 capitalize">{user.role || 'owner'} · {salonName || user.salon_name || 'Salon'}</p>
                    </div>
                  </div>
                </div>
                <DropdownMenuItem onClick={() => { openProfileModal(); }} className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10 focus:text-[#1a1a2e]" data-testid="dropdown-my-profile">
                  <User className="w-4 h-4" />
                  {t('my_profile')}
                </DropdownMenuItem>
                {canAccessSettings && (
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10 focus:text-[#1a1a2e]">
                    <SettingsIcon className="w-4 h-4" />
                    {t('settings')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10 focus:text-[#1a1a2e]">
                    <Globe className="w-4 h-4" />
                    {t('lang_title')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="rounded-xl border-gray-100">
                    {languages.map((lang) => (
                      <DropdownMenuCheckboxItem
                        key={lang.code}
                        checked={language === lang.code}
                        onCheckedChange={() => setLanguage(lang.code)}
                        className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10"
                      >
                        <span className={language === lang.code ? 'font-semibold text-[#f0a500]' : ''}>{lang.label}</span>
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={openLogoutConfirm} className="rounded-lg gap-2 cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700" data-testid="dropdown-logout">
                  <LogOut className="w-4 h-4" />
                  {t('nav_logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex sm:hidden items-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 bg-[#f0a500] text-[#1a1a2e] font-bold">
                    {(user.name || 'User').trim()[0]?.toUpperCase() || 'U'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[min(100vw-2rem,280px)] rounded-2xl border-gray-100 shadow-lg">
                  <div className="px-3 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-10 h-10 bg-[#f0a500] rounded-full flex items-center justify-center text-[#1a1a2e] font-bold text-sm">
                        {(user.name || 'User').trim()[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a1a2e]">{user.name || 'User'}</p>
                        <p className="text-xs text-gray-500 capitalize">{user.role || 'owner'} · {salonName || user.salon_name || 'Salon'}</p>
                      </div>
                    </div>
                  </div>
                  <DropdownMenuItem onClick={() => { openProfileModal(); }} className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10">
                    <User className="w-4 h-4" />
                    {t('my_profile')}
                  </DropdownMenuItem>
                  {canAccessSettings && (
                    <DropdownMenuItem onClick={() => navigate('/settings')} className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10">
                      <SettingsIcon className="w-4 h-4" />
                      {t('settings')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className="rounded-lg gap-2 cursor-pointer focus:bg-[#f0a500]/10">
                      <Globe className="w-4 h-4" />
                      {t('lang_title')}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="rounded-xl">
                      {languages.map((lang) => (
                        <DropdownMenuCheckboxItem
                          key={lang.code}
                          checked={language === lang.code}
                          onCheckedChange={() => setLanguage(lang.code)}
                          className="rounded-lg gap-2 cursor-pointer"
                        >
                          {lang.label}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={openLogoutConfirm} className="rounded-lg gap-2 cursor-pointer text-red-600 focus:bg-red-50">
                    <LogOut className="w-4 h-4" />
                    {t('nav_logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <Dialog open={profileModalOpen} onOpenChange={setProfileModalOpen}>
          <DialogContent className="bg-[#faf8f5] border border-gray-100 rounded-2xl shadow-xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="font-poppins text-lg sm:text-xl text-[#1a1a2e]">{t('my_profile')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid gap-2">
                <p className="text-sm text-gray-500">{t('name')}</p>
                <p className="text-base font-medium text-[#1a1a2e]">{user.name || '—'}</p>
              </div>
              <div className="grid gap-2">
                <p className="text-sm text-gray-500">{t('email')}</p>
                <p className="text-base font-medium text-[#1a1a2e]">{user.email || '—'}</p>
              </div>
              <div className="grid gap-2">
                <p className="text-sm text-gray-500">{t('staff_role')}</p>
                <p className="text-base font-medium text-[#1a1a2e] capitalize">{user.role || '—'}</p>
              </div>
              <div className="grid gap-2">
                <p className="text-sm text-gray-500">{t('settings_salon_name')}</p>
                <p className="text-base font-medium text-[#1a1a2e]">{salonName || user.salon_name || '—'}</p>
              </div>
              <div className="border-t border-gray-100 pt-4">
                {!showChangePassword ? (
                  <Button type="button" variant="outline" className="w-full rounded-xl border-[#f0a500]/30 text-[#1a1a2e] hover:bg-[#f0a500]/10" onClick={() => setShowChangePassword(true)}>
                    {t('change_password')}
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-[#1a1a2e] mb-1">{t('current_password')}</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full h-12 px-4 rounded-xl border border-gray-300 focus:border-[#f0a500] focus:ring-[#f0a500] bg-white text-base"
                        placeholder={t('current_password')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1a1a2e] mb-1">{t('new_password')}</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full h-12 px-4 rounded-xl border border-gray-300 focus:border-[#f0a500] focus:ring-[#f0a500] bg-white text-base"
                        placeholder={t('new_password')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#1a1a2e] mb-1">{t('confirm_new_password')}</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full h-12 px-4 rounded-xl border border-gray-300 focus:border-[#f0a500] focus:ring-[#f0a500] bg-white text-base"
                        placeholder={t('confirm_new_password')}
                      />
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row gap-2">
                      <Button type="button" variant="ghost" className="rounded-xl flex-1 w-full sm:w-auto" onClick={() => { setShowChangePassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}>
                        {t('cancel')}
                      </Button>
                      <Button type="button" className="rounded-xl flex-1 w-full sm:w-auto bg-[#f0a500] hover:bg-[#d99400] text-[#1a1a2e]" onClick={handleSavePassword} disabled={passwordSaving}>
                        {passwordSaving ? t('saving') : t('save')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={logoutConfirmOpen} onOpenChange={setLogoutConfirmOpen}>
          <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-poppins">{t('logout_confirm_title')}</AlertDialogTitle>
              <AlertDialogDescription>{t('logout_confirm')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleLogout} className="bg-red-500 hover:bg-red-600 rounded-xl" data-testid="logout-confirm-btn">
                {t('confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={deleteAllConfirmOpen} onOpenChange={setDeleteAllConfirmOpen}>
          <AlertDialogContent className="rounded-2xl max-w-md w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-poppins">{t('notif_delete_all_confirm_title')}</AlertDialogTitle>
              <AlertDialogDescription>{t('notif_delete_all_confirm_body')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAllNotifications} className="bg-red-500 hover:bg-red-600 rounded-xl">
                {t('notif_delete_all_confirm_btn')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
